import * as fs from "node:fs";
import * as path from "node:path";
import type { Nodes, Parent, Root } from "mdast";
import { fromMarkdown } from "mdast-util-from-markdown";

/**
 * One row in the doc reference-extraction report — describes a single
 * inline-code symbol mention found in a markdown page and whether
 * that symbol is present in the supplied `knownSymbols` list.
 */
export interface DocReferenceRecord {
  /**
   * Path to the markdown file the mention was found in. Resolved
   * relative to the configured `docsRoot` so reports are stable
   * across machines (no absolute prefixes leak through).
   */
  readonly docPath: string;

  /**
   * 1-indexed line number of the inline-code span in the markdown
   * source.
   */
  readonly line: number;

  /**
   * 1-indexed column where the inline-code span starts (the position
   * of the opening backtick).
   */
  readonly column: number;

  /**
   * The exact symbol text inside the backticks, e.g. `analyzeTsDocCoverage`.
   * Whitespace inside the inline-code span is preserved as-is and
   * the candidate is included only when it matches the configured
   * identifier pattern.
   */
  readonly symbol: string;

  /**
   * `true` when the symbol appears in the supplied `knownSymbols`
   * set. The downstream scan phase (#519) treats `false` as an
   * orphan reference candidate (advisory).
   */
  readonly isKnown: boolean;
}

/**
 * Options accepted by {@link extractDocReferences}.
 */
export interface ExtractDocReferencesOptions {
  /**
   * Absolute path to the docs root that should be walked for
   * `*.md` files. Defaults to `docs/src/content/docs/` relative
   * to the current working directory when omitted.
   *
   * @default "docs/src/content/docs"
   */
  readonly docsRoot?: string;

  /**
   * List of known public symbols to cross-index inline-code
   * mentions against. Typically the `symbol` column of the
   * report returned by `analyzeTsDocCoverage()` plus, optionally,
   * any extra names sourced from the api-extractor `.api.md`
   * rollup. Order is irrelevant; duplicates are deduplicated.
   */
  readonly knownSymbols?: ReadonlyArray<string>;

  /**
   * Regular expression a candidate must match to be considered a
   * TypeScript-identifier-shaped symbol. The default rejects
   * obvious non-symbol prose tokens (paths with slashes, prose
   * with spaces, lowercase one-letter words) while accepting the
   * three common identifier shapes used in the monorepo:
   * camelCase (`analyzeTsDocCoverage`), PascalCase
   * (`TsDocCoverageRecord`), and UPPER_SNAKE_CASE (`MAX_RETRIES`).
   *
   * Override this only if a project uses an unusual identifier
   * style — a custom pattern that is too permissive will produce
   * a noisy backlog of false positives.
   *
   * @default /^(?:[a-z][a-zA-Z0-9]*|[A-Z][a-zA-Z0-9]*|[A-Z][A-Z0-9_]*)$/
   */
  readonly symbolPattern?: RegExp;
}

const DEFAULT_DOCS_ROOT = "docs/src/content/docs";

/**
 * Default identifier pattern. Accepts the three common identifier
 * shapes used in this monorepo and rejects everything else.
 *
 * - `analyzeTsDocCoverage` (camelCase) — at least one character.
 * - `TsDocCoverageRecord` (PascalCase) — at least one character.
 * - `MAX_RETRIES` (UPPER_SNAKE_CASE) — only digits and underscores after the first uppercase letter.
 */
const DEFAULT_SYMBOL_PATTERN =
  /^(?:[a-z][a-zA-Z0-9]*|[A-Z][a-zA-Z0-9]*|[A-Z][A-Z0-9_]*)$/;

/**
 * Walks every `*.md` file under the configured docs root and
 * returns one record per inline-code symbol mention.
 *
 * The walker is AST-based — markdown is parsed with
 * `mdast-util-from-markdown` and only `inlineCode` nodes are
 * inspected. This means fenced code blocks, indented code blocks,
 * and HTML comments are skipped natively and never produce false
 * positives. Mentions inside link text or list items are
 * extracted normally.
 *
 * Candidates are filtered with the configured `symbolPattern`
 * (defaults to TS-identifier shapes — camelCase, PascalCase,
 * UPPER_SNAKE_CASE) so prose tokens like flags, file paths, or
 * multi-word phrases never appear in the report.
 *
 * @param options - Extraction options. All fields are optional;
 *                  pass an empty object to use defaults.
 */
export function extractDocReferences(
  options: ExtractDocReferencesOptions = {},
): Array<DocReferenceRecord> {
  const docsRoot = path.resolve(options.docsRoot ?? DEFAULT_DOCS_ROOT);
  const symbolPattern = options.symbolPattern ?? DEFAULT_SYMBOL_PATTERN;
  const knownSymbols = new Set(options.knownSymbols ?? []);

  if (!fs.existsSync(docsRoot)) {
    return [];
  }

  const records: Array<DocReferenceRecord> = [];
  for (const absolutePath of walkMarkdownFiles(docsRoot)) {
    const docPath = toPosix(path.relative(docsRoot, absolutePath));
    const source = fs.readFileSync(absolutePath, "utf-8");
    const tree = fromMarkdown(stripYamlFrontmatter(source));
    collectInlineCode(tree, (symbol, position) => {
      if (!symbolPattern.test(symbol)) {
        return;
      }
      records.push({
        docPath,
        line: position.line,
        column: position.column,
        symbol,
        isKnown: knownSymbols.has(symbol),
      });
    });
  }

  // Sort by docPath then line then column so the report is
  // deterministic across runs — downstream consumers (the scan
  // phase report writer in #519) depend on a stable ordering.
  records.sort((a, b) => {
    if (a.docPath !== b.docPath) {
      return a.docPath.localeCompare(b.docPath);
    }
    if (a.line !== b.line) {
      return a.line - b.line;
    }
    if (a.column !== b.column) {
      return a.column - b.column;
    }
    return a.symbol.localeCompare(b.symbol);
  });

  return records;
}

function walkMarkdownFiles(root: string): Array<string> {
  const out: Array<string> = [];
  const stack: Array<string> = [root];
  while (stack.length > 0) {
    const dir = stack.pop()!;
    let entries: Array<fs.Dirent>;
    try {
      entries = fs.readdirSync(dir, { withFileTypes: true });
    } catch {
      continue;
    }
    for (const entry of entries) {
      const full = path.join(dir, entry.name);
      if (entry.isDirectory()) {
        stack.push(full);
      } else if (entry.isFile() && full.toLowerCase().endsWith(".md")) {
        out.push(full);
      }
    }
  }
  // Sort here too so the final report ordering is independent of
  // the (platform-specific) directory-listing order.
  out.sort();
  return out;
}

interface InlineCodePosition {
  readonly line: number;
  readonly column: number;
}

function collectInlineCode(
  tree: Root,
  visit: (symbol: string, position: InlineCodePosition) => void,
): void {
  // Walk only nodes that can contain inline phrasing content.
  // `code` (fenced + indented) and `html` (HTML blocks +
  // comments) are *intentionally* never descended into — they
  // are the must-NOT-extract cases enumerated in the issue body.
  walk(tree);

  function walk(node: Nodes): void {
    if (node.type === "inlineCode") {
      const start = node.position?.start;
      if (!start) {
        return;
      }
      visit(node.value, { line: start.line, column: start.column });
      return;
    }
    if (node.type === "code" || node.type === "html") {
      return;
    }
    const parent = node as Partial<Parent>;
    if (Array.isArray(parent.children)) {
      for (const child of parent.children) {
        walk(child as Nodes);
      }
    }
  }
}

function toPosix(p: string): string {
  return p.split(path.sep).join("/");
}

/**
 * Strip leading YAML frontmatter (`--- ... ---`) from a markdown
 * source while preserving line numbers. Without the dedicated
 * frontmatter micromark extension, `mdast-util-from-markdown`
 * parses `---` as a thematic break and the YAML body as a
 * heading + paragraph, which leaks backtick-quoted values from
 * frontmatter into the inline-code report.
 *
 * The fence-content lines are replaced with blank lines (not
 * removed) so reported `line` numbers continue to point at the
 * correct position in the original source.
 */
function stripYamlFrontmatter(source: string): string {
  if (!source.startsWith("---")) {
    return source;
  }
  const lines = source.split("\n");
  if (lines[0] !== "---") {
    return source;
  }
  let endIndex = -1;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i] === "---") {
      endIndex = i;
      break;
    }
  }
  if (endIndex < 0) {
    return source;
  }
  for (let i = 0; i <= endIndex; i++) {
    lines[i] = "";
  }
  return lines.join("\n");
}
